import { PlusSquareOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Flex,
  Modal,
  Space,
  Table,
  TableProps,
  Tag,
  Typography,
} from 'antd';
import { BsToggles } from 'react-icons/bs';
import { FaRegEdit } from 'react-icons/fa';
import { MdOutlineLockReset } from 'react-icons/md';
import { RiDeleteBin5Fill } from 'react-icons/ri';
import { useParams } from 'react-router-dom';
import { TActionList } from '../../../../../common/types';

import {
  ActionListButton,
  AppDrawer,
  axiosApi,
  DataTable,
  drawerResource,
  openDrawer,
  useAppDispatch,
  useDrawer,
} from '../../../../../core';

import { useCallback, useMemo, useState } from 'react';
import { API_BASEURL, TFileInfo } from '../../../../../common';
import { PasswordModal } from '../../../components/password_modal';
import { UploadButton } from '../../import/components/UploadButton';
import {
  useGetBranchesQuery,
  useUpdateBranchMutation,
} from '../store/branch.api';
import { IMerchantBranchAttributes } from '../types';
import { CreateBranch } from './create';
import { UpdateBranch } from './update';

const { Text } = Typography;

export type TBranchDrawer = 'BranchDrawer';

export const BranchList: React.FC = () => {
  const { merchantId = '' } = useParams<string>();
  const {
    data: branches,
    isLoading,
    refetch,
  } = useGetBranchesQuery({ merchantId });
  const [updateBranch] = useUpdateBranchMutation();
  const dispatch = useAppDispatch();
  const { resource } = useDrawer();
  const { modal, message } = App.useApp();
  const [credentials, setCredentials] = useState([]);
  const [open, setOpen] = useState<boolean>(false);

  const actions: TActionList['items'] = [
    {
      label: 'Toggle Status',
      key: 'toggle_status',
      icon: <BsToggles />,
      action: (record) => {
        modal.confirm({
          title: 'Toggle branch status',
          content: 'Are you sure you want to toggle status?',
          okType: 'primary',
          onOk: () => {
            updateBranch({
              merchantId,
              id: record.key,
              status: !record.status,
            });
          },
        });
      },
    },
    {
      label: 'Reset Password',
      key: 'reset_password',
      icon: <MdOutlineLockReset />,
      action: (record) => {
        modal.confirm({
          title: 'Reset Password',
          content: 'Are you sure you want to reset password?',
          okType: 'primary',
          onOk: () => {
            axiosApi
              .post(
                `/merchants/${merchantId}/branches/${record.key}/reset-password`,
              )
              .then(({ data: response }) => {
                modal.success({
                  width: 500,
                  title: 'Success',
                  content: (
                    <PasswordModal
                      username={response.data.username}
                      password={response.data.password}
                    />
                  ),
                });
              })
              .catch((err) => console.log(err));
          },
        });
      },
    },
    {
      label: 'Edit',
      key: 'edit',
      icon: <FaRegEdit />,
      action: async (record) => {
        const { key: id, ...reset } = record;

        dispatch(
          drawerResource({
            id,
            type: 'MerchantBranch',
            attributes: reset,
          }),
        );
        dispatch(openDrawer('BranchDrawer'));
      },
    },
    {
      label: 'Delete',
      key: 'delete',
      icon: <RiDeleteBin5Fill />,
      disabled: true,
      action: (record) => {},
    },
  ];

  const columns: TableProps<IMerchantBranchAttributes>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status: boolean) => (
        <Tag color={status ? 'green' : 'red'}>
          {status ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
    },
    {
      title: 'Working Hours',
      dataIndex: 'working_hours',
    },
    {
      title: 'City',
      dataIndex: 'district',
      render: (district) => {
        return <Text>{district?.city.name}</Text>;
      },
    },
    {
      title: 'District',
      dataIndex: 'district',
      render: (district) => <Text>{district?.name}</Text>,
    },
    {
      align: 'center',
      width: 110,
      render: (record) => <ActionListButton items={actions} record={record} />,
    },
  ];

  const credentialColumns = useMemo(
    () => [
      {
        title: 'Branch',
        dataIndex: 'branch',
      },
      {
        title: 'Username',
        dataIndex: 'username',
        width: 120,
      },
      {
        title: 'Password',
        dataIndex: 'password',
        width: 120,
      },
    ],
    [],
  );

  const handleImport = (info: TFileInfo<{ data: [] }>) => {
    if (!info.file.response?.data?.length) {
      message.error('Error occured during importing the branches!');
      return;
    }

    message.success('The branches have been imported successfuly!');

    setCredentials(info.file.response?.data || []);
    setOpen(true);

    refetch();
  };

  const escapeCSVValue = useCallback((value: string): string => {
    if (value === null || value === undefined) {
      return '';
    }

    if (value.startsWith('0')) {
      return `="${value.replace(/"/g, '""')}"`;
    }

    return `="${value.replace(/"/g, '""')}"`;
  }, []);

  const copyContent = useCallback(async () => {
    try {
      const headers = credentialColumns.map((column) => column.title).join(',');

      const rows = credentials
        .map((row) =>
          credentialColumns
            .map((column) => escapeCSVValue(row[column.dataIndex]))
            .join(','),
        )
        .join('\n');

      const csvContent = `${headers}\n${rows}`;

      await navigator.clipboard.writeText(csvContent);
      message.success('Table content copied to clipboard!');
    } catch (err) {
      message.error('Failed to copy table content');
      console.error('Copy failed:', err);
    }
  }, [credentials, credentialColumns, message, escapeCSVValue]);

  return (
    <>
      <AppDrawer
        title={resource ? 'Update Branch' : 'Add New Branch'}
        drawerId={'BranchDrawer'}
      >
        {resource ? <UpdateBranch /> : <CreateBranch />}
      </AppDrawer>
      <Space direction="vertical">
        <Flex justify="end" gap={16}>
          <Space direction="horizontal">
            <Button
              type="primary"
              onClick={() => dispatch(openDrawer('BranchDrawer'))}
            >
              <PlusSquareOutlined /> Add Branch
            </Button>
            <UploadButton
              onFileUploaded={handleImport}
              label="Import Branches"
              action={`${API_BASEURL}/merchants/${merchantId}/branches/import`}
            />
          </Space>
        </Flex>
        <DataTable
          isLoading={isLoading}
          columns={columns}
          data={branches || []}
        />
        <Modal
          open={open}
          onCancel={() => setOpen(false)}
          footer={
            <Button
              type="primary"
              onClick={() => {
                copyContent();
                setOpen(false);
              }}
            >
              Copy & Close
            </Button>
          }
          maskClosable={false}
          closable={false}
        >
          <Table
            columns={credentialColumns}
            dataSource={credentials}
            pagination={false}
          />
        </Modal>
      </Space>
    </>
  );
};
